<script>
    import intersection from 'lodash/intersection';
    import SingleSelector from "../components/SingleSelector.vue";
    import RadioButtonGroup from '../components/RadioButtonGroup.vue';
    import CheckboxButtonGroup from '../components/CheckboxButtonGroup.vue';
    import RangeSlider from "../components/RangeSlider.vue";
    import bus from "../utils/eventBus";

    export default {
        components: {
            "single-selector": SingleSelector,
            "radio-button-group": RadioButtonGroup,
            "checkbox-button-group": CheckboxButtonGroup,
            "range-slider": RangeSlider
        },

        props: {
            showStationaryFilters: { type: Boolean, default: false },
            mobilityFilters: { type: Object, default: () => ({}) },
            primaryFilters: { type: Object, default: () => ({}) },
            secondaryFilters: { type: Object, default: () => ({}) },
            stationaryFilters: { type: Object, default: () => ({}) },
            mobileProducts: { type: Array, default: () => [] },
            stationaryProducts: { type: Array, default: () => [] },
            maxItemsPrint: { type: Number, default: 0 },
            pageLang: { type: String, default: '' },
            pageId: { type: String, default: '' }
        },
        data: function () {
            return {
                bus: bus,
                units: 'Metric',
                selectedFilters: {
                    mobility: this.showStationaryFilters ? '' : 'mobile',
                    primary: {},
                    secondary: {},
                    stationary: {}
                },
                productsInUse: this.showStationaryFilters ? this.stationaryProducts : this.mobileProducts
            };
        },
        created() {
            this.bus.$on('dropdownChange', (item) => {
                this.handleUnitsChange(item);
            });

            window.addEventListener('click', (e) => {
                this.bus.$emit('hideOtherDropdowns', e.target);
            });
        },
        mounted() {
            let query = {};
            if (this.$route && this.$route.query && Object.keys(this.$route.query).length > 0) {
                query = this.$route.query;
            } else if (this.$router && this.$router.currentRoute && this.$router.currentRoute.value && Object.keys(this.$router.currentRoute.value.query || {}).length > 0) {
                query = this.$router.currentRoute.value.query;
            } else {
                const rawSearch = window && window.location && window.location.search ? window.location.search : '';
                if (rawSearch && rawSearch.length > 1) {
                    const params = new URLSearchParams(rawSearch);
                    params.forEach((v, k) => { query[k] = v; });
                }
            }

            this.units = query.units || this.units;
            this.selectedFilters.mobility = this.showStationaryFilters ? (query.mobility ? query.mobility : '') : 'mobile';

            try {
                this.selectedFilters.primary = query.primaryFilters ? JSON.parse(query.primaryFilters) : {};
            } catch (e) {
                this.selectedFilters.primary = {};
            }
            try {
                this.selectedFilters.secondary = query.secondaryFilters ? JSON.parse(query.secondaryFilters) : {};
            } catch (e) {
                this.selectedFilters.secondary = {};
            }
            try {
                this.selectedFilters.stationary = this.showStationaryFilters && query.stationaryFilters ? JSON.parse(query.stationaryFilters) : {};
            } catch (e) {
                this.selectedFilters.stationary = {};
            }

            this.productsInUse = this.showStationaryFilters && query.mobility === 'stationary' ? this.stationaryProducts : this.mobileProducts;
        },
        updated() {
            const params = {
                mobility: this.selectedFilters.mobility
            };
            if (this.selectedFilters && this.selectedFilters.mobility === 'mobile') {
                params.primaryFilters = JSON.stringify(this.selectedPrimaryFilters || {});
                params.secondaryFilters = JSON.stringify(this.selectedSecondaryFilters || {});
            }
            else if (this.selectedFilters && this.selectedFilters.mobility === 'stationary') {
                params.stationaryFilters = JSON.stringify(this.selectedStationaryFilters || {});
            }
            params.units = this.units;
            this.$router.push({
                path: window.location.pathname,
                query: params
            }).catch((err) => { });
        },
        computed: {
            allFilters: function () {
                if (this.selectedFilters.mobility === 'mobile') {
                    return [...Object.values(this.primaryFilters), ...Object.values(this.secondaryFilters)];
                }
                else if (this.selectedFilters.mobility === 'stationary') {
                    return [...Object.values(this.stationaryFilters)];
                }
                else {
                    return [];
                }
            },
            allFiltersWithMeasurementSystem: function () {
                let filters = [];
                this.allFilters.forEach(filter => {
                    if (filter.measurementSystem !== 'None') {
                        filters.push(filter.name);
                    }
                });
                return filters;
            },
            selectedPrimaryFilters: function () {
                return this.getUsedSelectedFilters(this.selectedFilters.primary);
            },
            selectedSecondaryFilters: function () {
                return this.getUsedSelectedFilters(this.selectedFilters.secondary);
            },
            selectedStationaryFilters: function () {
                return this.getUsedSelectedFilters(this.selectedFilters.stationary);
            },
            selectedAllFilters: function () {
                if (this.selectedFilters.mobility === 'mobile') {
                    return { ...this.selectedPrimaryFilters, ...this.selectedSecondaryFilters };
                }
                else if (this.selectedFilters.mobility === 'stationary') {
                    return { ...this.selectedStationaryFilters };
                }
                else {
                    return {};
                }
            },
            selectedPrimaryFiltersCount: function () {
                return this.getFiltersCount(this.selectedPrimaryFilters);
            },
            selectedSecondaryFiltersCount: function () {
                return this.getFiltersCount(this.selectedSecondaryFilters);
            },
            selectedStationaryFiltersCount: function () {
                return this.getFiltersCount(this.selectedStationaryFilters);
            },
            products: function () {
                const productsSource = this.selectedFilters && this.selectedFilters.mobility === 'stationary'
                    ? this.stationaryProducts
                    : this.mobileProducts;

                const filterKeys = Object.keys(this.selectedAllFilters || {});

                if (filterKeys.length === 0) {
                    return productsSource;
                }

                return productsSource.filter(product => {
                    for (let i = 0; i < filterKeys.length; i++) {
                        const filterKey = filterKeys[i];
                        const filterValue = this.selectedAllFilters[filterKey];

                        // Do not filter out products without the filtered property
                        if (!product[filterKey]) {
                            continue;
                        }

                        // Sliders
                        if (typeof filterValue.min === 'number' && product[filterKey].value < filterValue.min) {
                            return false;
                        }
                        if (typeof filterValue.max === 'number' && product[filterKey].value > filterValue.max) {
                            return false;
                        }

                        // Single/multiple select
                        if (Array.isArray(filterValue) && filterValue.length > 0) {
                            const inter = intersection(filterValue, product[filterKey].value);
                            if (inter.length === 0) {
                                return false;
                            }
                        }
                    }

                    return true;
                });
            }
        },
        methods: {
            checkboxChange(e) {
                this.selectedFilters.primary = e;
            },
            handleMobilityChange() {
                this.onReset();
                this.$nextTick(() => {
                    initAccordions();
                })
            },
            onReset() {
                this.clearFilters('primary');
                this.clearFilters('secondary');
                this.showStationaryFilters && this.clearFilters('stationary');
                this.$emit('resetFilters');
            },
            getFiltersCount(filters) {
                let count = 0;

                if (!filters) {
                    return count;
                }

                const keys = Object.keys(filters);
                keys.forEach(key => {
                    const filter = filters[key];
                    count += filter.length > 0 ? filter.length : Object.keys(filter).length;
                });

                return count;
            },
            getUsedSelectedFilters(filters) {
                let usedFilters = {};

                if (!filters || typeof filters !== 'object') {
                    return usedFilters;
                }

                const keys = Object.keys(filters);
                keys.forEach(key => {
                    const val = filters[key];
                    if (val !== null && (val.length > 0 || val.min || val.max)) {
                        // Range slider
                        if (val.min || val.max) {
                            if (val.minDisabled && val.maxDisabled) {
                                usedFilters[key] = {};
                            }
                            else if (val.minDisabled) {
                                if (val.max) {
                                    usedFilters[key] = { max: val.max };
                                }
                            }
                            else if (val.maxDisabled) {
                                if (val.min) {
                                    usedFilters[key] = { min: val.min };
                                }
                            }
                            else {
                                if (val.min && val.max) {
                                    usedFilters[key] = { min: val.min, max: val.max };
                                }
                                else if (val.min) {
                                    usedFilters[key] = { min: val.min };
                                }
                                else if (val.max) {
                                    usedFilters[key] = { max: val.max };
                                }
                            }
                        }
                        else {
                            usedFilters[key] = val;
                        }
                    }
                });

                return usedFilters;
            },
            clearFilters(group, withUnitOnly) {
                const keys = Object.keys(this.selectedFilters[group]);
                keys.forEach(key => {
                    const filter = this.selectedFilters[group][key];
                    if (withUnitOnly && !this.allFiltersWithMeasurementSystem.includes(key)) {
                        return;
                    }

                    if (filter.min || filter.max) {
                        this.selectedFilters[group][key] = {};
                    } else {
                        this.selectedFilters[group][key] = [];
                    }
                });
            },
            handleUnitsChange(u) {
                this.onReset();
                if (u) {
                    this.units = u.id ? u.id : u;
                }
            },
            shouldBeDisabled(filter, option) {
                if (this.products.length > 0) {
                    var filtered = [];
                    for (let i = 0; i < this.products.length; i++) {
                        filtered = this.products[i][filter.name].value.filter(function (v) {
                            return v === option.value;
                        });
                        if (filtered.length) break;
                    }

                    if (filtered.length) {
                        return false;
                    }
                    else {
                        return true;
                    }
                }
                else {
                    return true;
                }
            },
            scrollToForm() {
                var inquiryForm = document.getElementsByClassName("industrysalesinquiryformblock")[0];
                if (typeof inquiryForm != 'undefined') {
                    inquiryForm.scrollIntoView({ behavior: "smooth" });
                }
            }
        }
    };
</script>